import { Input, InputNumber, Select, Typography } from "antd";
import { tenantAtom } from "atoms/tenantAtom";
import { STEP_INPUT_POLICIES } from "components/CC/config/stepInputPolicies";
import { useSelectedNode } from "components/CC/hooks/useDiagramActions";
import { webhookFormSchema, type WebhookFormData } from "components/CC/Nodes/WebhookNode/Forms/formSchema";
import { FormValueSync } from "components/CC/UI/FormValueSync";
import { useBuildParameterPickerCategories } from "components/CC/UI/ParameterPicker/useBuildParameterPickerCategories";
import { TagTextInput } from "components/CC/UI/TagInput/TagTextInput";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { TagSelect } from "components/UI/TagSelect/TagSelect.tsx";
import { TextArea } from "components/UI/TextArea/TextArea.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { useRecoilValue } from "recoil";

// Query param variables available for the endpoint URL
const AVAILABLE_VARIABLES = [
    { label: "eid", value: "eid" },
    { label: "advertiser_id", value: "advertiser_id" },
    { label: "external_id", value: "external_id" },
    { label: "advertiser_custom_id", value: "advertiser_custom_id" },
];

interface FormProps {
    onChange?: (data: WebhookFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formId: string;
}

export const WebhookForm = ({ onChange, onRegisterValidator, formId }: FormProps) => {
    const { selectedNode } = useSelectedNode();
    return (
        <GeneralFormProvider<WebhookFormData>
            key={selectedNode?.id}
            id={formId}
            schema={webhookFormSchema}
            defaultValues={selectedNode?.formData as WebhookFormData}
        >
            <FormContent onChange={onChange} onRegisterValidator={onRegisterValidator} />
        </GeneralFormProvider>
    );
};

interface FormContentProps {
    onChange?: (data: WebhookFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
}

const FormContent = ({ onChange, onRegisterValidator }: FormContentProps) => {
    const { control, watch, trigger } = useGeneralForm<WebhookFormData>();

    useEffect(() => {
        onRegisterValidator?.(() => trigger());
    }, [onRegisterValidator, trigger]);
    const { t } = usePaldockTranslation("components.connection-creator");
    const { selectedNode } = useSelectedNode();
    const buildCategories = useBuildParameterPickerCategories({ t, currentNodeId: selectedNode?.id });
    const tokenCategories = useMemo(
        () => buildCategories({ allowedCategories: [...STEP_INPUT_POLICIES.webhook.token.ui.allowedCategories] }),
        [buildCategories]
    );

    const waitOptions = useMemo(
        () => [
            { label: t("nodes.webhook.forms.wait_no"), value: false },
            { label: t("nodes.webhook.forms.wait_yes"), value: true },
        ],
        [t]
    );
    const tenant = useRecoilValue(tenantAtom);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    const token = watch("token");
    const wait = watch("wait");
    const baseApiUrl = import.meta.env.VITE_API_URL;

    const endpointUrl = useMemo(() => {
        const slug = tenant.slug_name ?? "{TENANT_SLUG}";
        const base = `${baseApiUrl}/${slug}/processes/webhook`;

        const params = new URLSearchParams();
        if (token) {
            params.set("id", token);
        }
        selectedTags.forEach((tag) => {
            params.set(tag, "REPLACE_ME");
        });

        const queryString = params.toString();
        return `${base}${queryString ? `?${queryString}` : ""}`;
    }, [baseApiUrl, tenant.slug_name, token, selectedTags]);

    return (
        <div className="bg-white rounded-lg p-4 m-3">
            <FormValueSync<WebhookFormData> onChange={onChange} />
            {/* Header with title and active toggle */}
            <div className="flex justify-between items-center mb-4">
                <Typography.Title level={4} className="!mb-0">
                    {t("nodes.webhook.forms.settings")}
                </Typography.Title>
                {/* Active toggle — hidden until BE is_enabled is wired up
                <Controller
                    control={control}
                    name="active"
                    render={({ field }) => (
                        <div className="flex items-center gap-2">
                            <Switch checked={field.value} onChange={field.onChange} />
                            <Typography.Text type="secondary">
                                {field.value ? t("nodes.webhook.forms.on") : t("nodes.webhook.forms.off")}
                            </Typography.Text>
                        </div>
                    )}
                /> */}
            </div>

            {/* Title and Subtitle row */}
            <div className="flex gap-4 mb-4">
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="title"
                    label={t("nodes.webhook.forms.title")}
                >
                    <Input placeholder={t("nodes.webhook.forms.title_placeholder")} />
                </FormItem>
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="subtitle"
                    label={t("nodes.webhook.forms.subtitle")}
                >
                    <Input placeholder={t("nodes.webhook.forms.subtitle_placeholder")} />
                </FormItem>
            </div>

            {/* Wait and Timeout row */}
            <div className="flex gap-4 mb-4">
                <Controller
                    control={control}
                    name="wait"
                    render={({ field }) => (
                        <div className="flex-1">
                            <Typography.Text className="block mb-1">{t("nodes.webhook.forms.wait")}</Typography.Text>
                            <Select
                                className="w-full"
                                options={waitOptions}
                                value={!!field.value}
                                onChange={field.onChange}
                            />
                        </div>
                    )}
                />
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="timeout"
                    label={t("nodes.webhook.forms.timeout")}
                >
                    <InputNumber min={0} max={wait ? 40 : undefined} className="w-full" disabled={!wait} />
                </FormItem>
            </div>

            {/* Token */}
            <div className="mb-4">
                <FormItem className="!mb-0" control={control} name="token" label={t("nodes.webhook.forms.token")}>
                    <TagTextInput
                        placeholder={t("nodes.webhook.forms.token_placeholder")}
                        categories={tokenCategories}
                        mode={STEP_INPUT_POLICIES.webhook.token.ui.mode}
                    />
                </FormItem>
            </div>

            {/* Endpoint (read-only, FE-generated for copying) */}
            <div className="mb-3">
                <Typography.Text className="block mb-1">{t("nodes.webhook.forms.endpoint")}</Typography.Text>
                <TextArea rows={3} appearance="readonly" readOnly value={endpointUrl} />
            </div>

            {/* Variable tags for endpoint query params */}
            <TagSelect options={AVAILABLE_VARIABLES} value={selectedTags} onChange={setSelectedTags} />
        </div>
    );
};
